/* eslint-disable camelcase */
/* eslint-disable array-callback-return */
import axios from 'axios';
import { useState } from 'react';
import { cloneDeep, clone } from 'lodash';
import utils from '../../utils/utils';
import ApiEndpoints from '../../const/ApiEndpoints';
import useDataIngestion from '../DM/useDataIngestion';

const BASE_URL = ApiEndpoints.MODULE_BASE_URL.DOC;
const totalPageCount = 'x-total-count';

/**
 * @function usePipeline
 * @description Custom hook for pipeline-related operations.
 * @returns {object} - Object containing functions for pipeline operations.
 */
export default function usePipeline() {
  const { markDatasetAsReadyToUse } = useDataIngestion();
  const [pipelineListTotalCount, setPipelineListTotalCount] = useState(0);
  const [pipelineListPageCount, setPipelineListPageCount] = useState(0);
  const [pipelineHistoryTotalCount, setPipelineHistoryTotalCount] = useState(0);
  const [pipelineHistoryPageCount, setPipelineHistoryPageCount] = useState(0);
  const [pipelineList, setpipelineList] = useState([]);
  const [totalPipelineList, settotalPipelineList] = useState(0);

  /**
   * @function fetchPipelines
   * @description Fetches pipelines for a  given project.
   * @param {number} projectId - The ID of the project.
   * @returns {Promise} - A promise that resolves with the fetched pipelines.
   * @throws Will throw an error if the   request fails.
   */
  const fetchPipelinesWithPagination = (
    project_id,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    startDate = '',
    endDate = '',
    sortField,
    sortOrder,
    field,
    isPublished = null
  ) => {
    // `https://api-qa.solytics.us/ad/automated_pipeline/allpipeline?projectId=43`
    return axios
      .get(BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_LIST, {
        params: {
          page: pageIndex + 1,
          ...(project_id ? { project_id } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(startDate !== '' ? { start_date: startDate } : {}),
          ...(endDate !== '' ? { end_date: endDate } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
          ...(field ? { fields: field } : {}),
          ...(isPublished !== null ? { is_published: isPublished } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setPipelineListTotalCount(res.headers[totalPageCount]);
            setPipelineListPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setPipelineListTotalCount(0);
            setPipelineListPageCount(0);
          }
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const fetchPipelines = (project_id, field, isPublished = null) => {
    // `https://api-qa.solytics.us/ad/automated_pipeline/show-pipeline?projectId=43`
    return axios
      .get(
        BASE_URL + ApiEndpoints.PIPELINE.GET_PIPELINE_LIST_WITHOUT_PAGINATION,
        {
          params: {
            project_id,
            ...(field ? { fields: field } : {}),
            ...(isPublished !== null ? { is_published: isPublished } : {}),
          },
        }
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function fetchPipelineHistory
   * @description Fetches the history of a pipeline.
   * @param {number} pipeline_id - The ID of the pipeline.
   * @returns {Promise} - A promise that resolves with the fetched pipeline history.
   * @throws Will throw an error if the request fails.
   */
  // https://api-qa.solytics.us/ad/automated_pipeline/pipeline-history?pipeline_id=4
  const fetchPipelineHistory = (
    project_id,
    pipeline_id,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    startDate = '',
    endDate = '',
    execution_id,
    field
  ) => {
    return axios
      .get(BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_HISTORY, {
        params: {
          project_id,
          page: pageIndex + 1,
          ...(pipeline_id ? { pipeline_id } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(startDate !== '' ? { start_date: startDate } : {}),
          ...(endDate !== '' ? { end_date: endDate } : {}),
          ...(execution_id ? { execution_id } : {}),
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setPipelineHistoryTotalCount(res.headers[totalPageCount]);
            setPipelineHistoryPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setPipelineHistoryTotalCount(0);
            setPipelineHistoryPageCount(0);
          }
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createPipeline
   * @description Creates a new pipeline.
   * @param {Object} body The data for creating the pipeline.
   * @returns {Promise} A promise that resolves with the response data if the pipeline is successfully created, otherwise rejects with an error.
   */
  const createPipeline = (body) => {
    // https://api-qa.solytics.us/ad/automated_pipeline/pipeline
    return axios
      .post(BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_OPERATIONS, body)
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  // TriggerPipeline
  /**
   * @function triggerPipeLine
   * @description Triggers a pipeline with the given ID.
   * @param {number} pipeline_id - The ID of the pipeline to trigger.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   */
  const triggerPipeline = (
    pipelineId,
    projectId,
    pipelineWorker,
    meta_data,
    pipelineLevelArguments
  ) => {
    // https://api-qa.solytics.us/ad/automated_pipeline/triggerPipeLine?pipeline_id=4
    return axios
      .post(
        BASE_URL + ApiEndpoints.PIPELINE.TRIGGER_PIPELINE,
        {
          pipeline_id: pipelineId,
          project_id: projectId,
          pipeline_worker: pipelineWorker === '' ? null : pipelineWorker,
          meta_data,
          ...(pipelineLevelArguments
            ? { pipeline_arguments: pipelineLevelArguments }
            : {}),
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  // Clone Pipeline
  const clonePipeline = (body) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DI + ApiEndpoints.PIPELINE.CLONE_PIPELINE,
        body
      )
      .then(
        (resp) => {
          return resp;
        },
        (error) => {
          throw error;
        }
      );

  // Share Pipeline
  const sharePipeline = (body) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DI + ApiEndpoints.PIPELINE.SHARE_PIPELINE,
        body
      )
      .then(
        (resp) => {
          return resp;
        },
        (error) => {
          throw error;
        }
      );

  // Fetch shared pipleline list

  const fetchSentReceivedPipeline = () =>
    axios.get(BASE_URL + ApiEndpoints.PIPELINE.FETCH_SHARED_PIPELINE).then(
      (resp) => {
        return resp;
      },
      (error) => {
        throw error;
      }
    );

  const fetchSentReceivedPipelinePaginated = (
    pageIndex = 0,
    pageSize = 20,
    searchText = '',
    sortField = null,
    sortOrder = null,
    status = 'Sent',
    createdBy = null
  ) =>
    axios
      .get(BASE_URL + ApiEndpoints.PIPELINE.FETCH_SHARED_PIPELINE_PAGINATED, {
        params: {
          page_index: pageIndex + 1,
          ...(createdBy ? { created_by: createdBy } : {}),
          ...(status ? { status } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
          ...(searchText ? { search_term: searchText } : {}),
          ...(sortField
            ? { sort_field: sortField }
            : { sort_field: 'shared_dt' }),
          ...(sortOrder ? { sort_order: sortOrder } : { sort_order: 'desc' }),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setpipelineList(res?.data);
            settotalPipelineList(res.headers[totalPageCount]);
            setPipelineListPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setpipelineList([]);
            settotalPipelineList(0);
            setPipelineListPageCount(0);
          }
          return res;
        },
        (error) => {
          setpipelineList([]);
          settotalPipelineList(0);
          setPipelineListPageCount(0);
          throw error;
        }
      );

  // For
  const onSharedPipelineAction = (payload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DI +
          ApiEndpoints.PIPELINE.PIPELINE_SHARE_ACTION,
        payload
      )
      .then(
        (resp) => {
          return resp;
        },
        (error) => {
          throw error;
        }
      );
  // Create New Node
  /**
   * @function createPipelineNode
   * @description Creates a pipeline node with the provided data.
   * @param {object} body - The data to be sent in the request body.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  // @ts-ignore
  const createPipelineNode = (body) => {
    // https://api-qa.solytics.us/ad/automated_pipeline/node
    return axios
      .post(BASE_URL + ApiEndpoints.PIPELINE.NODE_OPERATIONS, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function fetchNodeDetail
   * @description Fetches the details of a pipeline node with the provided data.
   * @param {object} project_id - The project_id to be sent in the request body.
   * @param {object} node_id - The node_id to be sent in the request body.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */

  const fetchNodeDetail = (projectId, nodeId, pipelineId) => {
    // 'https://api-dev.solytics.us/ad/automated-pipeline/node?project_id=&node_id=';
    return axios
      .get(BASE_URL + ApiEndpoints.PIPELINE.NODE_OPERATIONS, {
        params: {
          project_id: projectId,
          node_id: nodeId,
          pipeline_id: pipelineId,
        },
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function updatePipelineNode
   * @description Updates a pipeline node with the provided data.
   * @param {object} body - The data to be sent in the request body.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const updatePipelineNode = (body) => {
    const nodeCopy = cloneDeep(body);
    // react-flow not allowing updates to level one properties in object hence adding this inside data.
    // hence all this
    if (nodeCopy?.data?.documentation_detail) {
      nodeCopy.documentation_detail = cloneDeep(
        nodeCopy?.data?.documentation_detail
      );
      delete nodeCopy?.data?.documentation_detail;
    }
    if (nodeCopy?.data?.modelMetaDataDetail) {
      nodeCopy.modelMetaDataDetail = cloneDeep(
        nodeCopy?.data?.modelMetaDataDetail
      );
      delete nodeCopy?.data?.modelMetaDataDetail;
    }
    // https://api-qa.solytics.us/ad/automated_pipeline/node
    return axios
      .patch(BASE_URL + ApiEndpoints.PIPELINE.NODE_OPERATIONS, nodeCopy)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  // Read All Node

  /**
   * @function readAllNodesOfPipeLine
   * @description Reads all nodes of a pipeline with the given ID.
   * @param {number} pipeline_id - The ID of the pipeline.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const readAllNodesOfPipeLine = (pipeline_id) => {
    // https://api-qa.solytics.us/ad/automated_pipeline/readAllNode?pipeline_id=4
    return axios
      .get(BASE_URL + ApiEndpoints.PIPELINE.Read_ALL_NODE_OF_PIPELINE, {
        params: { pipeline_id },
      })
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  // ReadPipeLine
  /**
   * @function readPipeLine
   * @description Reads a pipeline with the given ID.
   * @param {number} pipeline_id - The ID of the pipeline.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const readPipeLine = (pipeline_id, project_id, field) => {
    // https://api-qa.solytics.us/ad/automated_pipeline/pipeline
    return axios
      .get(BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_OPERATIONS, {
        params: {
          pipeline_id,
          project_id,
          ...(field ? { fields: field } : {}),
        },
      })
      .then((resp) => {
        if (resp?.data?.pipelines?.flow_display?.nodes?.length) {
          const defectiveNodes = new Set(resp?.data?.pipelines?.defective_node);
          const nodes = resp?.data?.pipelines?.flow_display?.nodes?.map(
            (node) => {
              if (defectiveNodes.has(node?.node_name) && node?.parentNodeName) {
                defectiveNodes.add(node.parentNodeName);
              }
              return node;
            }
          );
          const updatedNodes = nodes?.map((node) => {
            return {
              ...node,
              data: {
                ...node.data,
                defective: defectiveNodes.has(node?.node_name),
              },
            };
          });
          // eslint-disable-next-line no-param-reassign
          resp.data.pipelines.flow_display.nodes = [...updatedNodes];
          // eslint-disable-next-line no-param-reassign
          resp.data.pipelines.defective_node = [...defectiveNodes];
          return resp;
        }
        return resp;
      })
      .catch((e) => {
        throw e;
      });
  };

  // UpdatePipeline
  /**
   * @function updatePipeLine
   * @description Updates a pipeline with the provided data and ID.
   * @param {object} body - The data to be sent in the request body.
   * @param {number} pipeline_id - The ID of the pipeline.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const updatePipeLine = (body, pipeline_id) => {
    const bodyClone = cloneDeep(body);
    // this is because react-flow not allowing updates in level 1 properties of node object.
    // useNodesState has some bug
    if (bodyClone?.flow_display?.nodes?.length) {
      bodyClone.flow_display.nodes = bodyClone?.flow_display?.nodes?.map(
        (node) => {
          const nodeCopy = clone(node);
          if (nodeCopy?.data?.documentation_detail) {
            delete nodeCopy?.data?.documentation_detail;
          }
          if (nodeCopy?.data?.modelMetaDataDetail) {
            nodeCopy.modelMetaDataDetail = cloneDeep(
              nodeCopy?.data?.modelMetaDataDetail
            );
            delete nodeCopy?.data?.modelMetaDataDetail;
          }

          return nodeCopy;
        }
      );
    }
    return axios
      .patch(BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_OPERATIONS, {
        ...bodyClone,
        pipeline_id,
      })
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  // GetNodeDataa
  /**
   * @function readNodeData
   * @description Reads data of a node with the given ID.
   * @param {number} nodeId - The ID of the node.
   * @returns {Promise} - A Promise that resolves with the response data if successful.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const readNodeData = (nodeId) => {
    // https://api-qa.solytics.us/ad/automated_pipeline/readNode?nodeId=11
    return axios.get(BASE_URL + ApiEndpoints.PIPELINE.READ_NODE_DATA, {
      params: { nodeId },
    });
  };

  /**
   * @function compilePipeline
   * @description Compiles a pipeline with the provided data.
   * @param {object} pipelineData - The data of the pipeline.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const compilePipeline = (
    pipelineData,
    project_id,
    pipelineLevelArguments
  ) => {
    const steps = pipelineData?.flow_display?.nodes
      ?.filter(
        (obj) =>
          (obj?.extent !== 'parent' && obj?.node_name) ||
          obj?.type === 'resizeRotate'
      )
      ?.map((node) => {
        return {
          node_name: node.node_name,
        };
      });

    const connections = pipelineData?.flow_display?.edges
      ?.map((edge) => {
        const nativeEdgesStart = pipelineData?.flow_display?.nodes?.find(
          (node) => node?.extent !== 'parent' && node.id === edge.source
        )?.node_name;
        const nativeEdgesEnd = pipelineData?.flow_display?.nodes?.find(
          (node) => node?.extent !== 'parent' && node.id === edge.target
        )?.node_name;
        const connectedPiplineEdgesStart =
          pipelineData?.flow_display?.nodes?.find(
            (node) => node?.extent === 'parent' && node.id === edge.source
          )?.parentNodeName;
        const connectedPiplineEdgesEnd =
          pipelineData?.flow_display?.nodes?.find(
            (node) => node?.extent === 'parent' && node.id === edge.target
          )?.parentNodeName;

        return {
          start_node_name: nativeEdgesStart || connectedPiplineEdgesStart,
          end_node_name: nativeEdgesEnd || connectedPiplineEdgesEnd,
        };
      })
      ?.filter((item) => item.start_node_name !== item.end_node_name);

    const connectedPipelines = pipelineData?.flow_display?.nodes
      ?.filter((obj) => obj?.type === 'resizeRotate')
      ?.map((node) => {
        return {
          pipeline_name: node.node_name,
        };
      });

    return axios
      .post(BASE_URL + ApiEndpoints.PIPELINE.COMPILE, {
        pipeline_id: pipelineData.pipeline_id,
        steps,
        connections,
        pipeline_name: connectedPipelines,
        project_id,
        ...(pipelineLevelArguments
          ? { pipeline_arguments: pipelineLevelArguments }
          : {}),
      })
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function deletePipeLine
   * @description Deletes a pipeline with the given ID.
   * @param {number} payload - Object for The ID of the pipeline to delete and ID of active project.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const deletePipeLine = (payload) => {
    return axios
      .delete(BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_OPERATIONS, {
        data: payload,
      })
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function deleteNode
   * @description Deletes a node from the pipeline with the given IDs.
   * @param {number} pipeline_id - The ID of the pipeline.
   * @param {number} nodeId - The ID of the node to delete.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const deleteNode = (pipeline_id, nodeId, project_id) => {
    return axios
      .delete(BASE_URL + ApiEndpoints.PIPELINE.NODE_OPERATIONS, {
        data: { pipeline_id, delete_node_id: nodeId, project_id },
      })
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  // https://api-qa.solytics.us/ad/automated_pipeline/deletepipeLineHistory?id=28
  /**
   * @function deletePipelineHistory
   * @description Deletes a pipeline history entry with the given ID.
   * @param {number} historyId - The ID of the history entry to delete.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const deletePipelineHistory = (historyId, project_id) => {
    return axios
      .delete(BASE_URL + ApiEndpoints.PIPELINE.DELETE_HISTORY, {
        data: { id: historyId, project_id },
      })
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function stopExecution
   * @description Stops the execution of a pipeline with the given history ID and payload.
   * @param {number} historyId - The ID of the pipeline history.
   * @param {object} payload - The data payload to be sent in the request body.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const stopExecution = (historyId, payload, project_id) => {
    return axios
      .post(BASE_URL + ApiEndpoints.PIPELINE.STOP_EXECUTION, {
        ...payload,
        pipeline_history_id: historyId,
        project_id,
      })
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function fetchNormalOperationsTaskList
   * @description Fetches the list of normal operations tasks with the provided payload.
   * @param {object} payload - The data payload to be sent in the request body.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const fetchNormalOperationsTaskList = (payload) => {
    return axios
      .post(
        BASE_URL + ApiEndpoints.PIPELINE.FETCH_NORMAL_OPERATION_TASKS,
        payload
      )
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function fetchAsyncResultNormalOperations
   * @description Fetches the asynchronous result of normal operations with the provided payload.
   * @param {object} payload - The data payload containing module, sub_module, and task_id.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const fetchAsyncResultNormalOperations = (payload) => {
    const url = utils.getModuleTaskListUrl(payload.module, payload.sub_module);
    return axios
      .get(`${url}/${payload.task_id}`)
      .then((res) => {
        return { ...res.data, checkurl: url };
      })
      .catch((e) => {
        throw e;
      });
  };

  const fetchAsyncResultSplit = (moduleName, subModuleName, task_id, type) => {
    const url = utils.getModuleTaskListUrl(moduleName, subModuleName);
    return axios.get(`${url}/${task_id}/${type}/`).then((res) => {
      return res;
    });
  };

  const onConnectPipeline = (body) => {
    return axios
      .post(BASE_URL + ApiEndpoints.PIPELINE.CONNECT_PIPELINE, {
        ...body,
      })
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  const onRemoveConnectPipeline = (payload) => {
    return axios
      .delete(BASE_URL + ApiEndpoints.PIPELINE.REMOVE_PIPELINE_CONNECTION, {
        data: { ...payload },
      })
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  const markAllDatasetAsReadyToUse = (body) =>
    axios
      .post(`${BASE_URL + ApiEndpoints.PIPELINE.ALL_DATASETS_RTU}`, body)
      .then(
        (res) => res,
        (err) => {
          throw err;
        }
      );

  /**
   * Retrieves the details of a dataset by its ID.
   *
   * @param {string} datasetId - The ID of the dataset to retrieve details for.
   * @param {object} [fields] - Optional fields to include in the response.
   * @return {object} The dataset details.
   */
  const getDatasetDetailsById = (datasetId, fields) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.PIPELINE.GET_DATASET_DETAILS}`, {
        params: {
          dataset_id: datasetId,
          ...(fields ? { fields } : {}),
        },
      })
      .then(
        (res) => res,
        (err) => {
          throw err;
        }
      );

  const onAddTableChartClickToMMD = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.MT + ApiEndpoints.MT.ADD_TABLE_AND_CHART
        }`,
        payload
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  const onSaveConnectedPipelineArguemnts = (payload) =>
    axios
      .post(
        `${BASE_URL + ApiEndpoints.PIPELINE.IMPORTED_PIPELINE_ARGUMENT}`,
        payload
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  const fetchConnectedPipelineArguemnts = (payload) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.PIPELINE.IMPORTED_PIPELINE_ARGUMENT}`, {
        params: {
          pipeline_id: payload?.pipeline_id,
          execution_id: payload?.execution_id,
        },
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  const fetchPipelineArguemntsMappingData = (pipeline_id) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.PIPELINE.PIPELINE_ARGUMENTS_MAPPING}`, {
        params: {
          pipeline_id,
        },
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  return {
    fetchPipelines,
    deleteNode,
    fetchPipelineHistory,
    createPipeline,
    triggerPipeline,
    clonePipeline,
    sharePipeline,
    pipelineList,
    totalPipelineList,
    setpipelineList,
    fetchSentReceivedPipeline,
    fetchSentReceivedPipelinePaginated,
    createPipelineNode,
    readAllNodesOfPipeLine,
    readPipeLine,
    updatePipeLine,
    readNodeData,
    updatePipelineNode,
    fetchNodeDetail,
    compilePipeline,
    deletePipeLine,
    deletePipelineHistory,
    stopExecution,
    fetchNormalOperationsTaskList,
    fetchAsyncResultNormalOperations,
    markAllDatasetAsReadyToUse,
    markDatasetAsReadyToUse,
    onSharedPipelineAction,
    onConnectPipeline,
    onRemoveConnectPipeline,
    pipelineListTotalCount,
    pipelineListPageCount,
    pipelineHistoryTotalCount,
    pipelineHistoryPageCount,
    fetchPipelinesWithPagination,
    getDatasetDetailsById,
    onAddTableChartClickToMMD,
    onSaveConnectedPipelineArguemnts,
    fetchConnectedPipelineArguemnts,
    fetchAsyncResultSplit,
    fetchPipelineArguemntsMappingData,
  };
}
